import type { ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router';
import { Layout } from './components/Layout';
import { Loading } from './components/ui';
import { SessionProvider, useSession } from './lib/session';
import { Competitors } from './pages/Competitors';
import { ContentHome } from './pages/content/ContentHome';
import { Editor } from './pages/content/Editor';
import { ProjectPage } from './pages/content/ProjectPage';
import { PublicationPage } from './pages/content/PublicationPage';
import { Reframe } from './pages/content/Reframe';
import { TemplateEditorPage } from './pages/content/TemplateEditor';
import { TemplatesPage } from './pages/content/TemplatesPage';
import { Jobs } from './pages/Jobs';
import { Login } from './pages/Login';
import { Merge } from './pages/Merge';
import { Overview } from './pages/Overview';
import { Placeholder } from './pages/Placeholder';
import { Pricing } from './pages/pricing/Pricing';
import { QuotationReview } from './pages/pricing/QuotationReview';
import { ProspectPage } from './pages/prospects/ProspectPage';
import { Prospects } from './pages/prospects/Prospects';
import { Reports } from './pages/Reports';
import { SettingsPage } from './pages/Settings';
import { StudioPage } from './pages/Studio';
import { WatchList } from './pages/WatchList';

function RequireUser({ children }: { children: ReactNode }) {
  const { user } = useSession();
  const location = useLocation();
  if (user === undefined) return <Loading what="Checking your session" />;
  if (user === null) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  return <>{children}</>;
}

export function App() {
  return (
    <SessionProvider>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route
          element={
            <RequireUser>
              <Layout />
            </RequireUser>
          }
        >
          <Route index element={<Overview />} />
          <Route path="competitors" element={<Competitors />} />
          <Route path="competitors/merge" element={<Merge />} />
          <Route path="competitors/:id" element={<StudioPage />} />
          <Route path="watchlist" element={<WatchList />} />
          <Route path="jobs" element={<Jobs />} />
          <Route path="jobs/:jobId" element={<Jobs />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="pricing" element={<Pricing />} />
          <Route path="pricing/quotations/:id" element={<QuotationReview />} />
          <Route path="content" element={<ContentHome />} />
          <Route path="content/projects/:id" element={<ProjectPage />} />
          <Route path="content/items/:id" element={<Editor />} />
          <Route path="content/publications/:id" element={<PublicationPage />} />
          <Route path="content/prepare" element={<Reframe />} />
          <Route path="content/templates" element={<TemplatesPage />} />
          <Route path="content/templates/:id" element={<TemplateEditorPage />} />
          <Route path="prospects" element={<Prospects />} />
          <Route path="prospects/:id" element={<ProspectPage />} />
          <Route path="reports" element={<Reports />} />
          <Route path="reports/:id" element={<Reports />} />
          <Route path="*" element={<Placeholder module="notfound" />} />
        </Route>
      </Routes>
    </SessionProvider>
  );
}
